import React, { useId, useState } from 'react';
import cx from 'classnames';
import Image from 'next/legacy/image';

import { BaseInputProps, TextInputWidth } from '../shared';

import icon from '@/images/error_icon.svg';

interface DateProps extends BaseInputProps {
  borderColor?: string;
  handleOnChange: (event: React.ChangeEvent, value: string) => void;
}

export function DateInput({
  value,
  label,
  placeholder = 'MM / DD / YYYY',
  error,
  required,
  autoComplete,
  handleOnChange,
  disabled = false,
  width = TextInputWidth.FULL,
  borderColor = '#00D9B4'
}: DateProps) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(value?.toString() ?? '');
  const inputId = useId();
  const float = !!inputValue || isFocused;

  const formatInputValue = (val: string) => {
    const cleanValue = val.replace(/\D/g, '').slice(0, 8); // Only allow 8 digits
    const parts = cleanValue.match(/(\d{0,2})(\d{0,2})(\d{0,4})/);
    if (!parts) return '';
    let formatted = '';
    if (parts[1]) formatted += parts[1];
    if (parts[2]) formatted += ' / ' + parts[2];
    if (parts[3]) formatted += ' / ' + parts[3];
    return formatted;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatInputValue(e.currentTarget.value);
    setInputValue(formattedValue);
    if (handleOnChange) handleOnChange(e, formattedValue);
  };

  return (
    <>
      <div
        className={cx(
          'text-input-wrapper',
          'relative h-16 rounded-input-box-shape p-0 transition duration-300',
          'border',
          error ? 'border-hibiscus' : null
        )}
        style={{
          width: `${width}%`,
          borderColor: error ? undefined : borderColor,
          marginBottom: error ? '8px' : '16px'
        }}
      >
        <label
          className={cx(
            'absolute rounded-input-box-shape font-medium transition-all duration-300',
            float
              ? 'top-0 -mt-2 ml-4 translate-y-0 bg-white px-1 text-[0.8em]'
              : 'top-1/2 -translate-y-1/2 pl-4 text-[1em]'
          )}
          htmlFor={inputId}
        >
          {label} {required ? '*' : ''}
        </label>
        <input
          id={inputId}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete ?? 'off'}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          aria-required={required}
          aria-invalid={!!error}
          data-testid={`input-date-${label}`}
          inputMode="numeric"
          className={cx(
            'bg-transparent h-full w-full rounded-input-box-shape border-0 outline-none transition',
            'px-4 text-[1em] leading-[1.5]',
            'disabled:text-dark-gray',
            float ? 'placeholder:opacity-100' : 'placeholder:opacity-0'
          )}
        />
      </div>
      <div
        className={cx(
          'error-message flex items-center overflow-hidden text-hibiscus',
          error ? 'mb-4 max-h-[24px] opacity-100' : 'mb-0 max-h-0 opacity-0'
        )}
        data-testid={`input-date-${label}-error`}
      >
        <div className="ml-4 mr-2 flex h-full">
          <Image src={icon} alt="Form input error" />
        </div>
        {error || ''}
      </div>
    </>
  );
}
